import { useState } from "react";
import { KeyRound, ShieldCheck, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Loading from "@/components/Loading";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { MfaCard } from "./MfaCard";
import {
  isPasskeySupported,
  enrollPasskey,
  enrollTotp,
  verifyTotp,
  type TotpEnrollment,
} from "../mfa";

const TOTP_CODE_LENGTH = 6;

const errorMessage = (e: unknown, fallback: string) =>
  e instanceof Error && e.message ? e.message : fallback;

/**
 * First-time enrollment: the user has no verified second factor. They choose
 * an authenticator app (TOTP) or a passkey (WebAuthn). On success the session
 * has a verified second factor and onComplete is called.
 */
export const MfaEnroll = ({ onComplete }: { onComplete: () => void }) => {
  const [method, setMethod] = useState<"totp" | "passkey" | null>(null);
  const [totp, setTotp] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startTotp = async () => {
    setError(null);
    setBusy(true);
    try {
      const enrollment = await enrollTotp();
      setTotp(enrollment);
      setMethod("totp");
    } catch (e) {
      setError(errorMessage(e, "Could not start authenticator setup."));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value: string) => {
    if (!totp || value.length !== TOTP_CODE_LENGTH) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await verifyTotp(totp.factorId, value);
      onComplete();
    } catch (e) {
      setCode("");
      setError(errorMessage(e, "That code wasn't valid. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const startPasskey = async () => {
    setError(null);
    setMethod("passkey");
    setBusy(true);
    try {
      await enrollPasskey();
      onComplete();
    } catch (e) {
      setMethod(null);
      setError(errorMessage(e, "Passkey setup was cancelled or failed."));
    } finally {
      setBusy(false);
    }
  };

  // TOTP: show the QR code + secret and collect the 6-digit code.
  if (method === "totp" && totp) {
    return (
      <MfaCard
        icon={<Smartphone size={56} />}
        title="Scan with your authenticator"
        description="Scan this QR code with an authenticator app (1Password, Google Authenticator, Authy), then enter the 6-digit code."
        error={error}
      >
        <div className="space-y-6">
          <div className="flex justify-center">
            <img
              src={totp.qrCode}
              alt="Authenticator QR code"
              className="h-48 w-48"
            />
          </div>
          <div className="text-center text-sm text-gray-600">
            Can't scan? Enter this key manually:
            <div className="mt-1 font-mono text-gray-800 break-all">
              {totp.secret}
            </div>
          </div>
          <div className="flex justify-center">
            <InputOTP
              value={code}
              onChange={(value) => {
                setCode(value);
                if (value.length === TOTP_CODE_LENGTH) {
                  submitCode(value);
                }
              }}
              disabled={busy}
              autoFocus
              maxLength={TOTP_CODE_LENGTH}
            >
              <InputOTPGroup>
                {Array.from({ length: TOTP_CODE_LENGTH }).map((_, i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          {busy && (
            <div className="flex justify-center">
              <Loading className="w-8 h-8" />
            </div>
          )}
        </div>
      </MfaCard>
    );
  }

  // Method picker.
  return (
    <MfaCard
      icon={<ShieldCheck size={56} />}
      title="Set up two-factor authentication"
      description="Your account requires a second factor. Choose how you'd like to verify it's you."
      error={error}
    >
      <div className="space-y-3">
        {isPasskeySupported() && (
          <Button
            onClick={startPasskey}
            disabled={busy}
            className="w-full h-14 justify-start gap-3"
          >
            <KeyRound className="h-5 w-5" />
            <span className="flex flex-col items-start">
              <span className="font-semibold">Use a passkey</span>
              <span className="text-xs font-normal opacity-80">
                Face ID, Touch ID, or a security key
              </span>
            </span>
            {busy && method === "passkey" && (
              <Loading className="w-5 h-5 ml-auto" />
            )}
          </Button>
        )}
        <Button
          onClick={startTotp}
          disabled={busy}
          variant="outline"
          className="w-full h-14 justify-start gap-3"
        >
          <Smartphone className="h-5 w-5" />
          <span className="flex flex-col items-start">
            <span className="font-semibold">Use an authenticator app</span>
            <span className="text-xs font-normal opacity-80">
              Get a code from an app like Google Authenticator
            </span>
          </span>
          {busy && method !== "passkey" && (
            <Loading className="w-5 h-5 ml-auto" />
          )}
        </Button>
      </div>
    </MfaCard>
  );
};
